import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Alert,
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	IconButton,
	Link,
	MenuItem,
	Paper,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import { listChatInstances, type LlmInstanceListItem, type LlmStatus } from '../../Services/chat';

const SSH_BASTION = (window as any).SSH_BASTION || '';
const SSH_PN_SUFFIX = (window as any).SSH_PN_SUFFIX || '';

const statusColors: Record<LlmStatus, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
	active: 'success',
	error: 'error',
	creating: 'warning',
	pending: 'info',
	deleted: 'default',
};

const statusChoices: LlmStatus[] = ['active', 'creating', 'pending', 'error', 'deleted'];

// #region formatDate
const formatDate = (dateStr: string) => {
	try {
		return new Date(dateStr).toLocaleString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return dateStr;
	}
};
// #endregion

// #region StatusChip
const StatusChip: React.FC<{ status: LlmStatus }> = ({ status }) => (
	<Chip
		label={status}
		color={statusColors[status] || 'default'}
		size="small"
		sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
	/>
);
// #endregion

// #region StatusSummary
const StatusSummary: React.FC<{ instances: LlmInstanceListItem[] }> = ({ instances }) => {
	const counts = useMemo(() => {
		return instances.reduce<Record<string, number>>((acc, i) => {
			acc[i.status] = (acc[i.status] || 0) + 1;
			return acc;
		}, {});
	}, [instances]);

	return (
		<Stack direction="row" spacing={1} alignItems="center">
			<Chip label={`${instances.length} instance${instances.length > 1 ? 's' : ''}`} size="small" />
			{statusChoices
				.filter((s) => counts[s])
				.map((s) => (
					<Chip key={s} label={`${counts[s]} ${s}`} color={statusColors[s]} size="small" variant="outlined" />
				))}
		</Stack>
	);
};
// #endregion

// #region InstanceRow
const InstanceRow: React.FC<{
	instance: LlmInstanceListItem;
	onOpenProject: (projectId: number) => void;
	onCopy: (text: string, label: string) => void;
}> = ({ instance, onOpenProject, onCopy }) => {
	const sshCmd =
		instance.slug && SSH_BASTION && SSH_PN_SUFFIX
			? `ssh -J ${SSH_BASTION} root@chat-${instance.slug}.${SSH_PN_SUFFIX}`
			: null;

	return (
		<TableRow hover>
			<TableCell>
				<Link
					component="button"
					type="button"
					onClick={() => onOpenProject(instance.projectId)}
					sx={{ textAlign: 'left' }}
				>
					{instance.projectName || `#${instance.projectId}`}
				</Link>
				<Typography variant="caption" color="text.secondary" display="block">
					<code>{instance.projectFolderId}</code>
				</Typography>
			</TableCell>
			<TableCell>
				<StatusChip status={instance.status} />
				{instance.errorMessage && (
					<Typography variant="caption" color="error" display="block">
						{instance.errorMessage}
					</Typography>
				)}
			</TableCell>
			<TableCell>
				{instance.domain ? (
					<Link
						href={`https://${instance.domain}`}
						target="_blank"
						rel="noopener"
						sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
					>
						{instance.domain}
						<OpenInNewIcon fontSize="small" />
					</Link>
				) : (
					'-'
				)}
			</TableCell>
			<TableCell>
				<Chip label={instance.llmModel} size="small" variant="outlined" />
				<Typography variant="caption" color="text.secondary" display="block">
					{instance.llmProvider}
				</Typography>
			</TableCell>
			<TableCell>{instance.region}</TableCell>
			<TableCell>
				<code style={{ fontSize: '0.85em' }}>{instance.publicIp || '-'}</code>
			</TableCell>
			<TableCell>{formatDate(instance.createdAt)}</TableCell>
			<TableCell align="right">
				<Stack direction="row" spacing={0.5} justifyContent="flex-end">
					{sshCmd && (
						<Tooltip title="Copier la commande SSH">
							<IconButton size="small" onClick={() => onCopy(sshCmd, 'Commande SSH copiée')}>
								<ContentCopyIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					<Tooltip title="Gérer l'instance">
						<IconButton size="small" onClick={() => onOpenProject(instance.projectId)}>
							<SettingsIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>
			</TableCell>
		</TableRow>
	);
};
// #endregion

// #region InstancesTable
const InstancesTable: React.FC<{
	instances: LlmInstanceListItem[];
	loading: boolean;
	onOpenProject: (projectId: number) => void;
	onCopy: (text: string, label: string) => void;
}> = ({ instances, loading, onOpenProject, onCopy }) => (
	<TableContainer component={Paper} variant="outlined">
		<Table size="small">
			<TableHead>
				<TableRow>
					<TableCell>Projet</TableCell>
					<TableCell>Statut</TableCell>
					<TableCell>Domaine</TableCell>
					<TableCell>Modèle</TableCell>
					<TableCell>Région</TableCell>
					<TableCell>IP publique</TableCell>
					<TableCell>Créé le</TableCell>
					<TableCell align="right">Actions</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{loading && instances.length === 0 ? (
					<TableRow>
						<TableCell colSpan={8} align="center">
							<CircularProgress size={24} sx={{ my: 2 }} />
						</TableCell>
					</TableRow>
				) : instances.length === 0 ? (
					<TableRow>
						<TableCell colSpan={8} align="center">
							Aucune instance LLM déployée
						</TableCell>
					</TableRow>
				) : (
					instances.map((instance) => (
						<InstanceRow
							key={instance.id}
							instance={instance}
							onOpenProject={onOpenProject}
							onCopy={onCopy}
						/>
					))
				)}
			</TableBody>
		</Table>
	</TableContainer>
);
// #endregion

// #region LlmInstancesList
export const LlmInstancesList: React.FC = () => {
	const navigate = useNavigate();
	const [instances, setInstances] = useState<LlmInstanceListItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [filterStatus, setFilterStatus] = useState('');
	const [search, setSearch] = useState('');
	const [snackbar, setSnackbar] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const res = await listChatInstances();
			setInstances(res.instances || []);
			setError(null);
		} catch (err: any) {
			setError(err?.error || err?.message || 'Échec du chargement des instances');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Un provisioning prend plusieurs minutes : on rafraîchit tant qu'une instance
	// est en cours de création, comme l'écran détail d'un projet.
	const hasPending = instances.some((i) => i.status === 'creating' || i.status === 'pending');
	useEffect(() => {
		if (!hasPending) return;
		const timer = setInterval(fetchData, 10000);
		return () => clearInterval(timer);
	}, [hasPending, fetchData]);

	// Filtrage client : au plus 1 instance par projet, le volume reste faible.
	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return instances.filter((i) => {
			if (filterStatus && i.status !== filterStatus) return false;
			if (!q) return true;
			return [i.projectName, i.projectFolderId, i.domain, i.slug, i.llmModel]
				.filter(Boolean)
				.some((v) => v.toLowerCase().includes(q));
		});
	}, [instances, filterStatus, search]);

	const handleCopy = useCallback((text: string, label: string) => {
		navigator.clipboard.writeText(text);
		setSnackbar(label);
	}, []);

	const handleOpenProject = useCallback((projectId: number) => navigate(`/projects/${projectId}/chat`), [navigate]);

	return (
		<Card sx={{ mt: 2 }}>
			<CardContent>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
					<Typography variant="h6">LLM Instances</Typography>
					<Stack direction="row" spacing={2} alignItems="center">
						<StatusSummary instances={instances} />
						<Tooltip title="Rafraîchir">
							<IconButton onClick={fetchData} disabled={loading}>
								<RefreshIcon />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
					<TextField
						select
						label="Statut"
						size="small"
						value={filterStatus}
						onChange={(e) => setFilterStatus(e.target.value)}
						sx={{ minWidth: 160 }}
					>
						<MenuItem value="">Tous</MenuItem>
						{statusChoices.map((s) => (
							<MenuItem key={s} value={s}>
								{s}
							</MenuItem>
						))}
					</TextField>
					<TextField
						label="Recherche (projet, domaine, modèle)"
						size="small"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						sx={{ minWidth: 320 }}
					/>
				</Stack>

				<InstancesTable
					instances={filtered}
					loading={loading}
					onOpenProject={handleOpenProject}
					onCopy={handleCopy}
				/>

				<Box sx={{ mt: 1 }}>
					<Typography variant="caption" color="text.secondary">
						{filtered.length} / {instances.length} affichée(s)
					</Typography>
				</Box>
			</CardContent>

			<Snackbar
				open={!!snackbar}
				autoHideDuration={3000}
				onClose={() => setSnackbar(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert severity="success" onClose={() => setSnackbar(null)}>
					{snackbar}
				</Alert>
			</Snackbar>
		</Card>
	);
};
// #endregion
